/**
 * Service for managing alert-related operations
 */
class AlertService {
  constructor(baseURL = process.env.REACT_APP_API_URL) {
    this.baseURL = baseURL;
    this.endpoints = {
      getUserAlerts: "api/alerts/user-alerts",
      getAlertsForUser: "api/alerts/for-user",
      markAlertRead: "api/alerts/mark-read",
      createAlert: "api/alerts/new-alert",
      getSingleAlert: "api/alerts/single-alert",
      updateAlert: "api/alerts/update-alert",
    };
  }

  /**
   * Get all alerts created by a specific user
   * @param {number} userId - The user's ID
   * @returns {Promise<Array>} - Array of alerts
   */
  async getUserAlerts(userId) {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.getUserAlerts}?user_id=${userId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch user alerts");
      }

      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error("Error fetching user alerts:", error);
      throw error;
    }
  }

  /**
   * Get alerts targeted for a specific user
   * @param {number} userId - The user's ID
   * @returns {Promise<Array>} - Array of alerts
   */
  async getAlertsForUser(userId) {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.getAlertsForUser}?user_id=${userId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch alerts for user");
      }

      const data = await response.json();

      if (!data.data || !Array.isArray(data.data)) {
        // eslint-disable-next-line
        console.warn("Invalid response format from getAlertsForUser:", data);
        return [];
      }

      // Process dates to ensure consistent format and handle boolean fields
      const processedAlerts = data.data.map(alert => ({
        ...alert,
        created_at: alert.created_at
          ? new Date(alert.created_at).toISOString()
          : null,
        // Ensure boolean fields are properly typed
        seen: Boolean(alert.seen),
        active: Boolean(alert.active),
        is_individual: Boolean(alert.is_individual),
      }));

      return processedAlerts;
    } catch (error) {
      console.error("Error fetching alerts for user:", error);
      throw error;
    }
  }

  /**
   * Mark an alert as read for a user
   * @param {number} userId - The user's ID
   * @param {number} alertId - The alert's ID
   * @returns {Promise<object>} - Response data
   */
  async markAlertRead(userId, alertId) {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.markAlertRead}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: userId,
            alert_id: alertId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to mark alert as read");
      }

      return await response.json();
    } catch (error) {
      console.error("Error marking alert as read:", error);
      throw error;
    }
  }

  /**
   * Create a new alert
   * @param {object} alertData - The alert data
   * @returns {Promise<object>} - The created alert
   */
  async createAlert(alertData) {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.createAlert}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(alertData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create alert");
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating alert:", error);
      throw error;
    }
  }

  /**
   * update existing alert
   * @param {object} alertData - The alert data
   * @returns {Promise<object>} - The updated alert
   */
  async updateAlert(alertData) {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.updateAlert}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(alertData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update alert");
      }

      return await response.json();
    } catch (error) {
      console.error("Error updating alert:", error);
      throw error;
    }
  }

  /**
   * Fetch a single alert for a specific user
   * @param {object} params - Parameters for fetching the alert
   * @param {number} params.user_id - The user's ID
   * @param {number} params.id - The alert's ID
   * @returns {Promise<object>} - The alert data returned by the API
   * @throws Will throw an error if the fetch operation fails
   */
  async getSingleAlert(params) {
    try {
      const { user_id, id } = params;

      const response = await fetch(
        `${this.baseURL}${this.endpoints.getSingleAlert}?user_id=${user_id}&id=${id}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch alert");
      }

      const responseData = await response.json();

      // Return the data, maintaing the API response structure
      return responseData;
    } catch (error) {
      console.error("Error fetching alert:", error);
      throw error;
    }
  }
}

// Create and export a default instance
const alertService = new AlertService();
export default alertService;

// Also export the class for cases where a custom instance is needed
export { AlertService };
